import { createQueryKeys } from '@lukemorales/query-key-factory'
import { useQuery } from 'react-query'
import { useAuth } from '~/hooks/useAuth'
import { ICompany, IPaginationParams } from '~/utils/types'
import {
  getAircraftById,
  getAircraftWithAudits,
  listAuditedAssets,
  listAuditedWithAssets,
} from '../data/aircraftApi'
import { useListPaginatedAircraft } from './useListPaginatedAircraft'

export const aircraftBaseKey = 'aircraft'

export const aircraftKeys = createQueryKeys(aircraftBaseKey, {
  list: (pagination: IPaginationParams, query?: string) => ({
    queryKey: [aircraftBaseKey, pagination, query],
  }),
})

export const useNonPaginatedAircraft = () =>
  useListPaginatedAircraft({
    limit: 999,
    offset: 0,
  })

export function useAircraft() {
  return {
    useListPaginatedAircraft,
    useNonPaginatedAircraft,
  }
}

export function useListAuditedAssets() {
  const { user } = useAuth()
  const companyId = (user?.company as ICompany)._id ?? ''

  return useQuery({
    queryKey: ['list_audited_aircrafts', companyId],
    queryFn: () => listAuditedAssets(companyId),
  })
}

interface UseListAircraftWithAuditProps {
  search?: string
  page: number
  limit: number
  asset_type?: string
}

export const useListAircraftWithAudit = ({
  search,
  page,
  limit,
  asset_type,
}: UseListAircraftWithAuditProps) => {
  const { user } = useAuth()
  const companyId = (user?.company as ICompany)._id ?? ''

  const {
    data: fetchedAircraftWithAudits,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['aircraft_with_audits', companyId, search, asset_type, page],
    queryFn: () => listAuditedWithAssets({ page, limit, search, asset_type }, companyId),
    staleTime: Infinity,
  })

  return {
    aircrafts: fetchedAircraftWithAudits?.docs,
    isLoading,
    isFetching,
    error,
    refetch,
    totalPages: fetchedAircraftWithAudits?.total_pages,
  }
}

export const useGetAircraftWithAudit = (aircraftId: string) => {
  const { user } = useAuth()
  const companyId = (user?.company as ICompany)._id ?? ''

  const { data: fetchedAircraftWithAudits, isLoading } = useQuery({
    queryKey: ['aircraft_with_audits', companyId, aircraftId],
    queryFn: () => getAircraftWithAudits(aircraftId, companyId),
    staleTime: Infinity,
  })

  return {
    fetchedAircraftWithAudits,
    isLoading,
  }
}

export const useAircraftById = (aircraftId: string) => {
  return useQuery({
    queryKey: ['aircraft_by_id'],
    queryFn: () => getAircraftById(aircraftId),
    enabled: Boolean(aircraftId),
    staleTime: 5 * 1000,
  })
}
